/**
 * Match pathname to a lazy page chunk and start loading it early (initial HTML + client navigations).
 * Uses webpack magic comments so the browser can prefetch the chunk.
 */
type PreloadEntry = {
  /** Return true if this chunk should load for `path` */
  match: (path: string) => boolean;
  load: () => Promise<unknown>;
};

const prefetch = (importer: () => Promise<unknown>) => () =>
  importer();

export const ROUTE_CHUNK_PRELOADS: PreloadEntry[] = [
  {
    match: (p) => p === '/',
    load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/MainSiteUnlockPage')),
  },
  {
    match: (p) => p === '/district',
    load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/DistrictHome')),
  },
  { match: (p) => p === '/explore', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ExplorePage')) },
  { match: (p) => p.startsWith('/market'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/Market')) },
  { match: (p) => p.startsWith('/connect'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ConnectPage')) },
  { match: (p) => p.startsWith('/community'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ConnectPage')) },
  { match: (p) => p.startsWith('/feed'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/DistrictFeed')) },
  { match: (p) => p.startsWith('/culture'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/CulturePage')) },
  { match: (p) => p.startsWith('/learn'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/LearnPage')) },
  { match: (p) => p.startsWith('/drops'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/NewDrops')) },
  { match: (p) => p === '/entry', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/DistrictEntryPage')) },
  { match: (p) => p === '/sell/onboard', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/SellerOnboardingPage')) },
  { match: (p) => p.startsWith('/sell'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/SellerDashboard')) },
  { match: (p) => p === '/stores' || p.startsWith('/store/'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/store/StorefrontDirectoryPage')) },
  { match: (p) => p.startsWith('/operations'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/AdminDashboard')) },
  { match: (p) => p.startsWith('/warehouse'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/WarehouseWmsPage')) },
  { match: (p) => p === '/clips', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ConnectPage')) },
  { match: (p) => p.startsWith('/profile'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ProfileNew')) },
  { match: (p) => p === '/register', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/RegisterPage')) },
  { match: (p) => p === '/forgot-password', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ForgotPasswordPage')) },
  { match: (p) => p === '/reset-password', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ResetPasswordPage')) },
  { match: (p) => p === '/login', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/LoginPage')) },
  { match: (p) => p.startsWith('/cart'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/CartPage')) },
  { match: (p) => p.startsWith('/checkout'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/CheckoutPage')) },
  { match: (p) => p.startsWith('/messages'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/ChatSystem')) },
  {
    match: (p) => /^\/(creator|collector|seller|curator)\//.test(p),
    load: prefetch(() => import(/* webpackPrefetch: true */ '../components/profile/PublicProfileRoute')),
  },
  { match: (p) => p === '/admin' || p.startsWith('/admin/'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/AdminDashboard')) },
  { match: (p) => p === '/post/new', load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/CreatePost')) },
  { match: (p) => p.startsWith('/post/'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/PostPage')) },
  { match: (p) => p.startsWith('/notifications'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/NotificationsPage')) },
  { match: (p) => p.startsWith('/progress'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/GamificationHub')) },
  { match: (p) => p.startsWith('/collector'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/CollectorHQ')) },
  { match: (p) => p.startsWith('/grail-hunts'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/GrailHuntsPage')) },
  { match: (p) => p.startsWith('/watch-part'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/WatchPartyPage')) },
  { match: (p) => p.startsWith('/vault'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/VaultPage')) },
  { match: (p) => p.startsWith('/about'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/AboutDistrict')) },
  { match: (p) => p.startsWith('/heart'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/EmotionalHubPage')) },
  { match: (p) => p.startsWith('/grow'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/GrowthHubPage')) },
  { match: (p) => p.startsWith('/settings'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/SettingsPage')) },
  { match: (p) => p.startsWith('/orders'), load: prefetch(() => import(/* webpackPrefetch: true */ '../pages/OrderHistory')) },
];

export function preloadChunkForPath(pathname: string): void {
  for (const entry of ROUTE_CHUNK_PRELOADS) {
    if (entry.match(pathname)) {
      entry.load().catch(() => {});
      return;
    }
  }
}

/** Prefetch without matching (hover intent) — fire-and-forget */
export function prefetchRoutePath(path: string): void {
  preloadChunkForPath(path);
}
